import React, { useState, useMemo, MouseEvent } from "react";
import { useSelector } from "react-redux";
import _map from "lodash/map";
import classNames from "classnames";

import { useAppSelector } from "@web-solutions/store/types";
import { PaymentSystem } from "@web-solutions/react-billing";

import { TM, t, tm } from "@web-solutions/module-localization";
import { useRemoteConfig } from "@web-solutions/base-app/hooks/use-remote-config";
import { LocalizedString } from "@web-solutions/core/constants/remote-config";

import { remoteConfigSelector } from "core/store/remote-config/selectors";
import { selectPaymentSystem } from "core/store/billing/selectors";
import {
  InjectedPaymentMethod,
  PaymentModalType,
} from "core/constants/remote-config";
import { CardFormLayout } from "core/payment/constants";
import { ProductInfo } from "core/payment/base/product-info";
import PaymentForm from "core/payment/components/payment-form";
import TrialPrice from "core/payment/base/product-info/components/trial-price";
import ApplePay from "core/payment/components/apple-pay";
import GooglePay from "core/payment/components/google-pay";
import { enrichStripeIntroProduct } from "core/utils/stripe-payment";
import { PayPalBtn } from "core/payment/components/paypal";
import { MercadoPagoBtn } from "core/payment/components/mercado-pago";
import TrialReminder from "core/payment/components/trial-reminder";
import { Title } from "core/ui-elements";

import { Options } from "core/payment";
import { type Subscription } from "core/interfaces/billing";

import InjectedPaymentMethodHeader from "core/summary/injected-payment-method/components/header";
import InjectedPaymentMethodSwitch from "core/summary/injected-payment-method/components/switch";

import { PaymentSafe } from "core/containers/payment-safe";

import {
  ProductDetails,
  selectInjectedPaymentMethod,
} from "core/store/billing/selectors";
import type { PaymentModalTitle } from "core/interfaces/payment-modal";

import PaymentDescription from "../../components/payment-description";
import { TotalPrice } from "../product-info/components/simple-view";
import { getActualPrices } from "../product-info/components/simple-view/utilts";

import { ICONS } from "./icons/cards";
import { ReactComponent as IconArrowDown } from "./icons/icon-arrow-down.svg";
import { ReactComponent as IconCreditCard } from "./icons/credit-card.svg";

import classes from "./style.module.scss";
import { useStore } from "../../../../../apps/virtual-brainary/src/onboarding/store/store";
import getLocale from "core/localization/get-locale";

const tKey = "core.payment_popup";

interface Props {
  activeProduct: ProductDetails;
  onSubmit: (formData: any) => Promise<void>;
  onSuccess: (purchase: Subscription) => void;
  onError: (error: any) => void;
  options: Options;
  showApplePay: boolean;
  showPayPal: boolean;
  showGooglePay: boolean;
  onApplePayClick: () => void;
  onApplePaySubmit: (formData: any) => Promise<void>;
  onGooglePayClick: () => void;
  onGooglePaySubmit: (formData: any) => Promise<void>;
  onPayPalSubmit: (formData: any) => Promise<void>;
  onPayPalSuccess: (p: Subscription) => void;
  onPayPalError: (e: any) => void;
  onUserInfoChange: (info: any) => void;
  isOnlyBaseModal?: boolean;
  title?: PaymentModalTitle;
  subtitle?: React.ReactNode;
  showCard?: boolean;
  showMercadoPago: boolean;
  upsaleTrigger?: string;
  onSaveTokenOneTime?: (tokenId: string) => void;
  showModalPopup?: boolean
}

function addDaysToDate(days: string): string {
  const daysNumber = parseInt(days, 10);
  if (isNaN(daysNumber)) {
    return '';
  }

  const currentDate = new Date();
  currentDate.setDate(currentDate.getDate() + daysNumber);

  const formatter = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const formattedDate = formatter.format(currentDate);
  console.log('formattedDate ', formattedDate);
  return formattedDate;
}

const BasePaymentModalBody: React.FC<Props> = ({
  activeProduct: rawActiveProduct,
  onSubmit,
  onSuccess,
  onError,
  isOnlyBaseModal,
  options,
  showApplePay,
  showPayPal,
  showGooglePay,
  onApplePayClick,
  onApplePaySubmit,
  onGooglePayClick,
  onGooglePaySubmit,
  onPayPalSubmit,
  onPayPalSuccess,
  onPayPalError,
  onUserInfoChange,
  title,
  subtitle,
  showCard = true,
  showMercadoPago,
  upsaleTrigger,
  onSaveTokenOneTime,
  showModalPopup
}) => {
  const paymentSystem = useSelector(selectPaymentSystem);

  // Defensive enrichment: if upstream missed the Stripe-intro override
  // (different rendering paths for Apple Pay vs card-only modal can produce
  // an unenriched product), apply it here so all downstream consumers
  // (ProductInfo, ApplePay, etc.) see intro + discount values. Use dynamic
  // override amount from product instead of hardcoded $0.99 for proper pricing.
  const isStripeFlow =
    paymentSystem === PaymentSystem.STRIPE || Boolean(rawActiveProduct?.stripeId);
  const introAmount = rawActiveProduct?.trialPriceAmount ? Number(rawActiveProduct.trialPriceAmount) : (rawActiveProduct?.amount ? Number(rawActiveProduct.amount) : (rawActiveProduct?.priceAmount ? Number(rawActiveProduct.priceAmount) : undefined));
  const activeProduct = isStripeFlow && !rawActiveProduct?.isTrial
    ? (enrichStripeIntroProduct(rawActiveProduct, introAmount) || rawActiveProduct)
    : rawActiveProduct;
  const {
    isShowSafeCheckout,
    showTermsAtPlans,
    cardFormCollapsed,
    cardFormLayout,
    isInjectedPaymentMethodOnPage,
    paymentModalType,
    isTrialPeriodReminder,
    simpleTitleModal,
    totalPriceType,
  } = useSelector(remoteConfigSelector);
  const hiddenAltMethods =
    isInjectedPaymentMethodOnPage && !activeProduct?.isOneTimePurchase;
  const [showCardForm, setShowCardForm] = useState(
    // Stripe's Google Pay deliberately does not count here. Whether that button
    // shows up is only known a moment later (see below), while this decision is
    // taken once, on mount — counting it would collapse the card form behind a
    // toggle for a button that may never appear, and on a funnel with no other
    // wallet that leaves the modal with no way to pay.
    !cardFormCollapsed
    || (!showApplePay && !showPayPal && !(showGooglePay && !isStripeFlow))
  );

  // Stripe's Google Pay button answers this itself — the wallet needs Chrome,
  // HTTPS and a card in the Google account, none of which the remote-config flag
  // knows about. Until it says yes, no slot and no "or" are drawn for a button
  // that never appears. Other systems keep going by the flag alone: their SDK
  // renders into the container below and reports nothing back.
  const [isStripeGooglePayReady, setIsStripeGooglePayReady] = useState(false);
  const showGooglePayMethod =
    showGooglePay && (!isStripeFlow || isStripeGooglePayReady);

  const state = useStore((state) => state);
  const { setPage, showDownSale } = state;

  const injectedPaymentMethod = useSelector(selectInjectedPaymentMethod);

  const injectedApplePay =
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_GOOGLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_PAYPAL;
  const injectedPayPal =
    injectedPaymentMethod === InjectedPaymentMethod.PAYPAL ||
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_PAYPAL ||
    injectedPaymentMethod === InjectedPaymentMethod.GOOGLEPAY_PAYPAL;
  const injectedGooglePay =
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_GOOGLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.GOOGLEPAY ||
    injectedPaymentMethod === InjectedPaymentMethod.GOOGLEPAY_PAYPAL;
  const injectedCard = injectedPaymentMethod === InjectedPaymentMethod.CARD;
  const injectedApplePayOrGooglePay =
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_GOOGLEPAY;
  const injectedGooglePayPayPal =
    injectedPaymentMethod === InjectedPaymentMethod.GOOGLEPAY_PAYPAL;
  const injectedApplePayPayPal =
    injectedPaymentMethod === InjectedPaymentMethod.APPLEPAY_PAYPAL;

  const modalType = isOnlyBaseModal
    ? PaymentModalType.BASE
    : paymentModalType;

  const isBaseModal = modalType === PaymentModalType.BASE;
  const isTopModal = modalType === PaymentModalType.TOP_INJECTED;
  const isSimpleModal = modalType === PaymentModalType.SIMPLE;
  const isSimpleTitleModal = modalType === PaymentModalType.SIMPLE_TITLE;
  const isBottomModal = modalType === PaymentModalType.BOTTOM_INJECTED;

  const isSimpleView = isSimpleModal || isSimpleTitleModal;

  // const locale: keyof LocalizedString = useAppSelector(
  //   (state) => state.locale.locale
  // );
  const locale = getLocale();
  const handlePayWithCardClick = (e: MouseEvent) => {
    e.preventDefault();
    if (showApplePay || showPayPal || showGooglePayMethod) {
      setShowCardForm(!showCardForm);
    }
  };

  const formWrapStyle = classNames(classes.formWrap, {
    [classes.formWrapOpen]: showCardForm || injectedCard,
  });

  const expandWrapStyle = classNames(classes.expandWrap, {
    [classes.expand]: showCardForm || injectedCard,
  });

  const color = useMemo(() => {
    const root = document.querySelector(":root") as HTMLElement;

    return getComputedStyle(root).getPropertyValue(
      "--injectedPaymentPrimaryColor"
    );
  }, []);

  const cardFormProps = useMemo(
    () => ({
      isSimpleView,
      product: activeProduct,
      color,
      options,
      paymentSystem,
      formClassName: "payment-form",
      fieldClassName: classNames("form-field", {
        "form-field-compact": cardFormLayout === CardFormLayout.COMPACT,
      }),
      onSubmit,
      onSuccess,
      onError,
      onApplePayClick,
      onApplePaySubmit,
      onGooglePayClick,
      onGooglePaySubmit,
      onUserInfoChange,
      onSaveTokenOneTime,
    }),
    [
      isSimpleView,
      color,
      activeProduct,
      options,
      cardFormLayout,
      paymentSystem,
      onSubmit,
      onSuccess,
      onError,
      onApplePayClick,
      onApplePaySubmit,
      onGooglePayClick,
      onGooglePaySubmit,
      onUserInfoChange,
      onSaveTokenOneTime,
    ]
  );

  const { fullPrice, actualPrice, vat, vatPrice } = getActualPrices({
    activeProduct,
    totalPriceType,
  });

  const { paymentPopup, newPaymentModal, specialOffer, products } =
    useRemoteConfig();

  // Every lookup below is optional-chained the whole way down. These read remote
  // config, where the arrays are empty by default and the nested keys may be
  // absent for a given funnel — and `showDownSale` is persisted in localStorage,
  // so a stale `true` from an earlier session lands here with
  // `specialOffer.products` still empty. A single unguarded `[locale]` on an
  // undefined object throws during render, and since this component IS the modal
  // body, the whole payment form silently fails to display.
  const downSaleProduct = specialOffer?.products?.[0];
  const defaultProduct = products?.[0];

  const productDescription = showDownSale
    ? {
        name: downSaleProduct?.name,
        plan_end_days: addDaysToDate(downSaleProduct?.plan_end_days),
        subTitle: downSaleProduct?.subTitle?.[locale],
        titleProduct: downSaleProduct?.title?.[locale],
        priceProduct: downSaleProduct?.priceStartTrial?.price,
      }
    : {
        name: defaultProduct?.name,
        plan_end_days: addDaysToDate(
          defaultProduct?.priceStartTrial?.plan_end_days
        ),
        subTitle: defaultProduct?.subTitle?.[locale],
        titleProduct: defaultProduct?.title?.[locale],
        priceProduct: defaultProduct?.priceStartTrial?.price?.[locale],
      };
  const { name, plan_end_days, subTitle, titleProduct, priceProduct } = productDescription;

  return (
    <div
      className={classNames(classes.contentContainer, {
        [classes.simple]: isSimpleView,
      })}
    >
      {isSimpleTitleModal && simpleTitleModal?.titleEnabled && (
        <header
          className={classes.header}
          style={
            !simpleTitleModal?.withTotalPriceUnder
              ? { display: "contents" }
              : {}
          }
        >
          <Title
            className={classes.title}
            style={
              simpleTitleModal?.fontSize
                ? { fontSize: `${simpleTitleModal.fontSize}px` }
                : {}
            }
          >
            {title?.simple_title || (
              <TM k={t(`${tKey}.simple_type.title_default`)} />
            )}
          </Title>
          {simpleTitleModal?.withTotalPriceUnder && (
            <TotalPrice
              className={classes.totalPrice}
              mode={simpleTitleModal?.type}
              vat={vat}
              vatPrice={vatPrice}
              fullPrice={fullPrice}
              actualPrice={actualPrice}
            />
          )}
        </header>
      )}
      <div
        className={classNames(classes.wrap, {
          [classes.simple_title]: isSimpleTitleModal,
        })}
      >
        {(isBaseModal || isBottomModal) && (
          <>
            <ProductInfo
              product={activeProduct}
              subtitle={subtitle}
              modalType={modalType}
            />
            {isTrialPeriodReminder && !activeProduct.isOneTimePurchase && (
              <TrialReminder className={classes.reminder} />
            )}
          </>
        )}
        {(isTopModal || isBottomModal) && (
          <>
            <InjectedPaymentMethodHeader isCentralHeader={isTopModal} />
            <InjectedPaymentMethodSwitch isBaseSwitch />
          </>
        )}

        {isSimpleView && (
          <>
            <InjectedPaymentMethodSwitch isBaseSwitch />
            <ProductInfo
              product={activeProduct}
              subtitle={subtitle}
              isSimpleView={isSimpleView}
              modalType={modalType}
            />
            <div className={classes.divider} />
          </>
        )}

        <div
          className={classNames(
            classes.payment,
            isBottomModal ? classes.row : classes.column
          )}
        >
          {isBottomModal && !injectedCard && (
            <TrialPrice
              textLineThrough={tm(
                activeProduct.textLineThrough,
                "",
                activeProduct
              )}
              trialPrice={activeProduct.trialPrice}
              className={classes.price}
            />
          )}

          {isTopModal && (
            <>
              <div className={classes.space} />
              <ProductInfo product={activeProduct} modalType={modalType} />
              {isTrialPeriodReminder && !activeProduct.isOneTimePurchase && (
                <TrialReminder className={classes.reminder} />
              )}
            </>
          )}

          <div
            className={classNames(classes.paymentMethods, {
              [classes.hidden]: hiddenAltMethods,
            })}
          >
            {((showApplePay && injectedApplePay) ||
              (showApplePay && isBaseModal)) && (
              <div
                style={{ marginTop: "16px" }}
                className={classes.buttonWrapper}
              >
                <ApplePay
                  activeProduct={activeProduct}
                  className={classes.appleButton}
                  onApplePayClick={onApplePayClick}
                  onApplePaySubmit={onApplePaySubmit}
                />
              </div>
            )}
            {
              <div
                className={classNames(classes.buttonWrapper, {
                  [classes.hidden]:
                    !showApplePay || !(injectedApplePay || isBaseModal),
                })}
              >
                <div id={options?.applePayButtonParams?.containerId}></div>
              </div>
            }

            {showApplePay &&
              ((isBaseModal && showCard) ||
                (injectedApplePayPayPal && showPayPal) ||
                (injectedApplePayOrGooglePay && showGooglePayMethod)) && (
                <div className={classes.or}>
                  <p>{paymentPopup.or[locale]}</p>
                </div>
              )}

            {/* Stripe's Google Pay button. Renders nothing for the systems whose
                SDK draws its own into the container below. Kept mounted on the
                flag alone — it is what reports back whether the wallet is there;
                the wrapper is what collapses when it is not. */}
            {showGooglePay && (injectedGooglePay || isBaseModal) && (
              <div
                className={classNames(classes.buttonWrapper, {
                  [classes.hidden]: !showGooglePayMethod,
                })}
              >
                <GooglePay
                  activeProduct={activeProduct}
                  className={classes.googleButton}
                  onGooglePayClick={onGooglePayClick}
                  onGooglePaySubmit={onGooglePaySubmit}
                  onAvailabilityChange={setIsStripeGooglePayReady}
                />
              </div>
            )}
            {
              <div
                className={classNames(classes.buttonWrapper, {
                  [classes.hidden]: !(injectedGooglePay || isBaseModal),
                })}
              >
                <div id={options?.googlePayButtonParams?.containerId}></div>
              </div>
            }

            {!injectedApplePayOrGooglePay &&
              showGooglePayMethod &&
              ((isBaseModal && showCard) || injectedGooglePayPayPal) && (
                <div className={classes.or}>
                  <p>{paymentPopup.or[locale]}</p>
                </div>
              )}

            {showPayPal && (injectedPayPal || isBaseModal) && (
              <>
                <PayPalBtn
                  product={activeProduct}
                  className={classes.paypal}
                  onSubmit={onPayPalSubmit}
                  onSuccess={onPayPalSuccess}
                  onError={onPayPalError}
                />
                {isBaseModal && showCard && (
                  <div className={classes.or}>
                    <p>{paymentPopup.or[locale]}</p>
                  </div>
                )}
              </>
            )}
            {showMercadoPago && (
              <>
                <MercadoPagoBtn
                  product={activeProduct}
                  upsaleTrigger={upsaleTrigger}
                />
                {isBaseModal && showCard && (
                  <div className={classes.or}>
                    <p>{paymentPopup.or[locale]}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div
          className={classNames(formWrapStyle, {
            [classes.hidden]: !showCard || !(injectedCard || isBaseModal),
            [classes.simple_view]: isSimpleView,
          })}
        >
          <button
            type="button"
            onClick={handlePayWithCardClick}
            className={classes.payWithCard}
          >
            <IconCreditCard
              width={30}
              height={21}
              className={classes.icon_card}
            />
            <p className={classes.payWithCardText}>
              {paymentPopup.payWithCard[locale]}
            </p>

            {/* delete arrow */}
            {(showApplePay || showPayPal || showGooglePayMethod) && !injectedCard && (
              <div className={classes.arrowDownWrap}>
                <IconArrowDown
                  width={12}
                  height={6}
                  className={classNames(classes.iconArrow, {
                    [classes.iconArrowUp]: showCardForm,
                  })}
                />
              </div>
            )}
          </button>
          <div className={expandWrapStyle}>
            <ul className={classes.cardsList}>
              {_map(ICONS, (Icon, key) => (
                <li key={key} className={classes.cardItem}>
                  <Icon width="100%" height="100%" />
                </li>
              ))}
            </ul>
            <PaymentForm {...cardFormProps} />
          </div>
        </div>

        {newPaymentModal && paymentPopup.showPrivacy && (
          <div className={classes.policy} style={{display: 'flex', flexDirection: 'column', marginTop: '20px', paddingBottom: '20px'}}>
            <span className={classes.privacy} style={{textAlign: 'left', marginLeft: 0}}>
              {t("onboarding.personalPlan.plan.accept")}{" "}
              <a style={{textDecoration: 'underline', fontWeight: 600}} onClick={() => setPage("privacy-policy")}>
                {t("onboarding.personalPlan.plan.privacy")}
              </a>
              ,{" "}
              <a style={{textDecoration: 'underline', fontWeight: 600}} onClick={() => setPage("terms-of-use")}>
                {t("onboarding.personalPlan.plan.terms")}
              </a>
            </span>

            {/* Subscription terms — trial price, renewal price, cancellation.
                An upsale is a single charge with none of that, so the offer
                window keeps only the Privacy Policy / Terms line above. */}
            {!activeProduct.isOneTimePurchase && (
              <span className={classes.privacy}>
                {t("onboarding.personalPlan.plan.planExpires")} {plan_end_days}.{" "}
                {t("onboarding.personalPlan.plan.harged")} {subTitle}{" "}
                {t("onboarding.personalPlan.plan.for")} {titleProduct},{" "}
                {t("onboarding.personalPlan.plan.then")} {String(priceProduct ?? "")}.{" "}
                {t("onboarding.personalPlan.plan.last")}
              </span>
            )}
          </div>
        )}
        {!activeProduct.isOneTimePurchase && (
          <>
            {isShowSafeCheckout ? <PaymentSafe /> : null}
            {!showTermsAtPlans && (
              <PaymentDescription activeProduct={activeProduct} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BasePaymentModalBody;
